<template>
  <v-dialog v-model="dialog" persistent scrollable max-width="900" :fullscreen="$vuetify.breakpoint.xsOnly">
    <v-card class="req-dialog">
      <v-form class="fdlg__form" @submit.prevent="saveRequest">
        <div class="fdlg__head">
          <div>
            <h2 class="fdlg__title">Demers nou</h2>
            <p class="fdlg__sub">Câmpurile cu * sunt obligatorii. După salvare, demersul pleacă la aprobare.</p>
          </div>
          <v-btn icon aria-label="Închide" @click="closeDialog()">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-card-text ref="body" class="fdlg__body">
          <section v-if="hasManyDepartments" class="form-section">
            <div class="form-section__label"><v-icon small>mdi-domain</v-icon>Pentru ce departament*</div>
            <div class="dept-picker" role="radiogroup" aria-label="Departamentul">
              <button
                v-for="d in myDepartments"
                :key="d.name"
                type="button"
                role="radio"
                class="dept-picker__option"
                :class="{ 'is-active': selectedDepartment === d.name }"
                :aria-checked="selectedDepartment === d.name"
                @click="selectedDepartment = d.name"
              >
                <v-icon class="dept-picker__radio">
                  {{ selectedDepartment === d.name ? "mdi-radiobox-marked" : "mdi-radiobox-blank" }}
                </v-icon>
                <span class="dept-picker__name">{{ d.name }}</span>
                <span v-if="d.isSef" class="dept-picker__tag">Șef</span>
              </button>
            </div>
            <div v-if="formErrors.selectedDepartment" class="form-error">{{ formErrors.selectedDepartment }}</div>
          </section>

          <section class="form-section">
            <div class="form-section__label"><v-icon small>mdi-desktop-classic</v-icon>Echipamentul</div>
            <v-row dense>
              <v-col cols="12" sm="5">
                <v-select
                  v-model="selectedEquipment"
                  :items="visibleEquipmentTypes"
                  label="Tip echipament*"
                  item-text="type"
                  item-value="id"
                  prepend-inner-icon="mdi-shape-outline"
                  outlined
                  @change="onEquipmentTypeChange"
                  :error-messages="formErrors.selectedEquipment"
                ></v-select>
              </v-col>
              <v-col cols="12" sm="7">
                <v-select
                  v-model="selectedRequest"
                  :items="causeItems"
                  label="Tip cerere*"
                  item-text="type"
                  item-value="id"
                  prepend-inner-icon="mdi-format-list-bulleted-type"
                  outlined
                  :disabled="!selectedEquipment"
                  :hint="selectedEquipment ? '' : 'Alegeți întâi tipul echipamentului'"
                  persistent-hint
                  no-data-text="Nu există cereri pentru acest tip"
                  :error-messages="formErrors.selectedRequest"
                >
                  <template v-slot:selection="{ item }">
                    <span class="selected-text">{{ item.type }}</span>
                    <span v-if="item.creates_equipment" class="cause-tag">echipament nou</span>
                  </template>
                </v-select>
              </v-col>

              <template v-if="isNewItem">
                <v-col cols="12">
                  <div class="new-item-note">
                    <v-icon small>mdi-information-outline</v-icon>
                    <span>
                      Echipamentul nou apare în <b>Echipamente</b> după ce demersul este <b>Îndeplinit</b>.
                      Nr. inventar îl poate completa DTI mai târziu.
                    </span>
                  </div>
                </v-col>
                <v-col cols="12" sm="7">
                  <v-combobox
                    v-model="newModel"
                    :search-input.sync="newModelSearch"
                    :items="modelSuggestions"
                    label="Model*"
                    prepend-inner-icon="mdi-tag-outline"
                    outlined
                    hint="Scrieți modelul sau alegeți unul folosit deja"
                    persistent-hint
                    :menu-props="{ offsetY: true, contentClass: 'menu-wrap' }"
                    :error-messages="formErrors.newModel"
                  ></v-combobox>
                </v-col>
                <v-col cols="12" sm="5">
                  <v-text-field
                    v-model="newRegistration"
                    label="Nr. inventar (opțional)"
                    prepend-inner-icon="mdi-barcode"
                    outlined
                    maxlength="150"
                  ></v-text-field>
                </v-col>
                <v-col cols="12">
                  <v-autocomplete
                    ref="location"
                    v-model="selectedLocation"
                    :items="locations"
                    label="Unde se livrează*"
                    item-text="label"
                    item-value="id"
                    prepend-inner-icon="mdi-map-marker-outline"
                    outlined
                    :menu-props="locationMenuProps"
                    :error-messages="formErrors.selectedLocation"
                    @focus="fitLocationMenu"
                  ></v-autocomplete>
                </v-col>
              </template>

              <template v-else>
                <v-col cols="12" sm="5">
                  <v-autocomplete
                    v-model="selectedModel"
                    :items="models"
                    label="Model*"
                    item-text="model"
                    item-value="model"
                    prepend-inner-icon="mdi-tag-outline"
                    outlined
                    :disabled="!selectedEquipment"
                    @change="fetchRegistrationNr"
                    :error-messages="formErrors.selectedModel"
                  ></v-autocomplete>
                </v-col>
                <v-col cols="12" sm="7">
                  <v-autocomplete
                    v-model="selectedRegistrationNr"
                    :items="registrations"
                    label="Nr. inventar / seria*"
                    item-text="searchText"
                    item-value="id"
                    prepend-inner-icon="mdi-barcode"
                    outlined
                    return-object
                    class="reg-select"
                    :menu-props="{ offsetY: true, contentClass: 'menu-wrap' }"
                    :disabled="!selectedModel"
                    :hint="registrations.length > 1 ? 'Puteți căuta și după seria, codul QR sau loc' : ''"
                    :error-messages="formErrors.selectedRegistrationNr"
                  >
                    <template v-slot:item="{ item }">
                      <v-list-item-content>
                        <v-list-item-title>{{ item.title }}</v-list-item-title>
                        <v-list-item-subtitle class="reg-choice__line">{{ item.place }}</v-list-item-subtitle>
                        <v-list-item-subtitle v-if="item.codes" class="reg-choice__line">{{ item.codes }}</v-list-item-subtitle>
                      </v-list-item-content>
                    </template>
                    <template v-slot:selection="{ item }">
                      <span class="selected-text">{{ item.title }} — {{ item.place }}</span>
                    </template>
                  </v-autocomplete>
                </v-col>
              </template>
            </v-row>
          </section>

          <section class="form-section">
            <div class="form-section__label"><v-icon small>mdi-message-text-outline</v-icon>Comentariu</div>
            <v-textarea
              v-model="body"
              :placeholder="isNewItem ? 'Pentru ce aveți nevoie de echipament' : 'Descrieți pe scurt problema sau ce aveți nevoie'"
              outlined
              auto-grow
              rows="3"
              hide-details
              class="mb-3"
            ></v-textarea>
            <PhotoInput
              v-model="photos"
              purpose="comment"
              :max="6"
              label="Fotografii (opțional)"
              hint="Fotografiați defectul; le vede oricine are acces la demers. Nu fotografiați etichetele cu parole — acestea se pun la echipament, în «Fotografie etichetă»."
              @uploading="photosUploading = $event"
              @error="$emit('error', $event)"
            />
          </section>
        </v-card-text>

        <div class="fdlg__foot">
          <span v-if="selectedDepartment" class="req-dialog__dept">
            <v-icon small>mdi-domain</v-icon>{{ selectedDepartment }}
          </span>
          <v-spacer></v-spacer>
          <v-btn text large @click="closeDialog()">Anulare</v-btn>
          <v-btn class="btn-green" large type="submit" :loading="saving" :disabled="photosUploading">
            <v-icon left>mdi-send-outline</v-icon>Trimite demersul
          </v-btn>
        </div>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script>
import * as api from "./api";
import * as helper from "@/helper/helper";
import { mapGetters } from "vuex";
import PhotoInput from "@/components/files/PhotoInput.vue";

/**
 * One item of the chosen model in the „Nr. inventar” list. Nr. inventar is optional (Inventariere, import), so
 * items of one model in one room are told apart by seria, the QR code (tag) and „Detalii amplasare”: an item
 * without nr. inventar leads with its seria (else its code). All of it is searchable.
 */
function itemChoice(reg) {
  const nr = String(reg.registration_number || "").trim();
  const serial = reg.serial_number || "";
  const tag = reg.tag ? String(reg.tag).toUpperCase() : "";
  const title = nr || (serial ? `Seria ${serial}` : tag ? `Cod ${tag}` : "fără nr. inventar");
  const codes = [nr && serial ? `Seria ${serial}` : "", tag && (nr || serial) ? `Cod ${tag}` : ""];
  return {
    ...reg,
    title,
    place: [reg.locationInfo, reg.location_details].filter(Boolean).join(" · "),
    codes: codes.filter(Boolean).join(" · "),
    searchText: [nr, serial, tag, reg.locationInfo, reg.location_details].filter(Boolean).join(" "),
  };
}

// Types and causes come from Configurare: a cause is offered for the types it lists; the ones that create
// equipment (Procurare / Eliberare of a new item) only for types that allow new items.
export default {
  components: { PhotoInput },
  data() {
    return {
      dialog: false,
      saving: false,
      // photos for the first step (uploaded already, linked when the demer is sent)
      photos: [],
      photosUploading: false,
      equipmentTypes: [],
      requestTypes: [],
      models: [],
      modelsKey: null,
      registrations: [],
      selectedDepartment: null,
      selectedEquipment: null,
      selectedRequest: null,
      selectedModel: null,
      selectedRegistrationNr: null,
      body: "",
      // new item
      modelSuggestions: [],
      suggestionsType: null,
      newModel: null,
      newModelSearch: null,
      newRegistration: "",
      locations: [],
      selectedLocation: null,
      locationMenuWidth: null,
      formErrors: this.emptyErrors(),
    };
  },
  computed: {
    ...mapGetters(["getUser"]),
    myDepartments() {
      return this.$store.getters.myDepartments;
    },
    hasManyDepartments() {
      return this.$store.getters.hasManyDepartments;
    },
    mainDepartment() {
      const main = this.myDepartments.find((d) => d.isPrimary) || this.myDepartments[0];
      return main ? main.name : null;
    },
    visibleEquipmentTypes() {
      return this.equipmentTypes.filter((type) => !type.is_hidden);
    },
    selectedType() {
      return this.equipmentTypes.find((type) => type.id === this.selectedEquipment) || null;
    },
    causesForType() {
      const typeId = this.selectedEquipment;
      if (!typeId) return [];
      return this.requestTypes.filter(
        (cause) =>
          cause.module === "equipment" &&
          !cause.is_hidden &&
          (cause.equipment_type_ids || []).includes(typeId)
      );
    },
    existingItemCauses() {
      return this.causesForType.filter((cause) => !cause.creates_equipment);
    },
    // printers & co. never offer a new item, even if a cause lists them
    newItemCauses() {
      if (!this.selectedType || !this.selectedType.allows_new_item) return [];
      return this.causesForType.filter((cause) => cause.creates_equipment);
    },
    typeOffersNewItem() {
      return this.newItemCauses.length > 0;
    },
    causeOptions() {
      return [...this.existingItemCauses, ...this.newItemCauses];
    },
    causeItems() {
      if (!this.typeOffersNewItem) return this.existingItemCauses;
      const items = [];
      if (this.existingItemCauses.length) {
        items.push({ header: "Pentru un echipament existent" }, ...this.existingItemCauses, { divider: true });
      }
      items.push({ header: "Echipament nou" }, ...this.newItemCauses);
      return items;
    },
    selectedCause() {
      return this.causeOptions.find((cause) => cause.id === this.selectedRequest) || null;
    },
    isNewItem() {
      return !!(this.selectedCause && this.selectedCause.creates_equipment);
    },
    // what is typed counts, even before the combobox commits it on blur
    newModelText() {
      const text = this.newModelSearch != null ? this.newModelSearch : this.newModel;
      return typeof text === "string" ? text.trim() : "";
    },
    currentModelsKey() {
      return `${this.selectedEquipment}|${this.selectedDepartment}`;
    },
    // long room names widened the list past the dialog: keep it as wide as the field and wrap them
    locationMenuProps() {
      return {
        offsetY: true,
        offsetOverflow: true,
        maxWidth: this.locationMenuWidth || "auto",
        contentClass: "menu-wrap",
      };
    },
  },
  methods: {
    emptyErrors() {
      return {
        selectedEquipment: "",
        selectedModel: "",
        selectedRegistrationNr: "",
        selectedDepartment: "",
        selectedRequest: "",
        newModel: "",
        selectedLocation: "",
      };
    },
    async fetchData() {
      try {
        const [equipmentTypes, requestTypes] = await Promise.all([
          api.fetchEquipmentTypes(),
          api.fetchRequestTypes(),
        ]);
        this.equipmentTypes = equipmentTypes;
        this.requestTypes = requestTypes;
        if (!this.mainDepartment) {
          const userData = await api.getUserById(this.getUser.userId);
          this.selectedDepartment = userData.department;
        } else if (!this.selectedDepartment) {
          this.selectedDepartment = this.mainDepartment;
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        this.$emit("error", "Tipurile de echipamente nu au putut fi încărcate. Reîncărcați pagina.");
      }
    },
    open() {
      if (!this.selectedDepartment) this.selectedDepartment = this.mainDepartment;
      // Configurare may have changed types, causes or rooms since the page was opened
      if (this.equipmentTypes.length) this.fetchData();
      this.locations = [];
      this.dialog = true;
      helper.scrollToTop(this);
    },
    /**
     * „Creează demers” from an item (Echipamente row, item card): the dialog opens on that exact item.
     * item = { id, equipmentTypeId, model, requestDepartment } (requestDepartment: one of the user's departments,
     * the one the item belongs to). The person still picks the cause; the usual circuit applies.
     */
    async openFor(item) {
      if (!item || !item.id) return this.open();
      this.clearFields();
      this.open();
      if (!this.equipmentTypes.length) await this.fetchData();
      if (item.requestDepartment) this.selectedDepartment = item.requestDepartment;
      // the department watcher runs first, while no type is chosen (it would clear the item)
      await this.$nextTick();
      this.selectedEquipment = item.equipmentTypeId;
      this.resetOwnedItem();
      await this.fetchModels();
      if (!this.models.some((m) => m.model === item.model)) return;
      this.selectedModel = item.model;
      await this.fetchRegistrationNr();
      const match = this.registrations.find((r) => r.id === item.id);
      if (match) this.selectedRegistrationNr = match;
    },
    async saveRequest() {
      if (this.saving || this.photosUploading) return;
      this.validateForm();

      if (!this.isFormValid()) {
        return;
      }

      this.saving = true;
      try {
        if (this.isNewItem) {
          await this.saveNewItemRequest();
        } else {
          await this.saveEquipmentRequest();
        }
      } finally {
        this.saving = false;
      }
    },
    async saveEquipmentRequest() {
      const equipmentId = this.selectedRegistrationNr.id;
      let equipmentInfo;
      try {
        equipmentInfo = await api.getEquipmentById(equipmentId);
      } catch (error) {
        console.error("Error fetching the equipment:", error);
        this.$emit("error", api.errorMessage(error, "Echipamentul nu a putut fi verificat. Încercați din nou."));
        return;
      }

      if (!equipmentInfo) {
        this.$emit("error", "Informațiile despre echipament nu au fost găsite!");
        return;
      }

      if (!equipmentInfo.isFunctional) {
        this.dialog = false;
        this.$emit("error", "Acest echipament nu este activ!");
        return;
      }

      // the server sets the author, the first stage and the rest from the login and the equipment; the comment
      // and photos are saved with it in one go, so a failure leaves no half-saved demer behind
      const postData = {
        requests_type_id: this.selectedRequest,
        equipment_id: equipmentId,
        department: this.selectedDepartment,
        comments: this.body,
        file_ids: this.photos.map((photo) => photo.id),
      };

      try {
        await api.saveRequestData(postData);
        this.$emit("success", "Demersul a fost trimis");
        this.$emit("updateRequestData");
        this.dialog = false;
        this.clearFields();
      } catch (error) {
        console.error("Error saving request:", error);
        this.$emit("error", api.errorMessage(error, "Demersul nu a fost salvat. Încercați din nou."));
      }
    },
    // the server writes the first response itself: nothing is posted to /responses here
    async saveNewItemRequest() {
      try {
        await api.saveNewItemRequest({
          equipment_type_id: this.selectedEquipment,
          requests_type_id: this.selectedRequest,
          model: this.newModelText,
          registration_number: this.newRegistration.trim(),
          location_id: this.selectedLocation,
          department: this.selectedDepartment,
          comments: this.body,
          file_ids: this.photos.map((photo) => photo.id),
        });
        this.$emit("success", "Demersul a fost trimis");
        this.$emit("updateRequestData");
        this.dialog = false;
        this.clearFields();
      } catch (error) {
        console.error("Error saving new-item request:", error);
        this.$emit("error", api.errorMessage(error, "Demersul nu a fost salvat. Încercați din nou."));
      }
    },
    onEquipmentTypeChange() {
      if (!this.causeOptions.some((cause) => cause.id === this.selectedRequest)) {
        this.selectedRequest = null;
      }
      this.resetOwnedItem();
      this.newModel = null;
      this.newModelSearch = null;
      this.formErrors.newModel = "";
      if (this.suggestionsType !== this.selectedEquipment) {
        this.modelSuggestions = [];
        this.suggestionsType = null;
      }
      this.refreshItemOptions();
    },
    resetOwnedItem() {
      this.selectedModel = null;
      this.selectedRegistrationNr = null;
      this.models = [];
      this.modelsKey = null;
      this.registrations = [];
      this.formErrors.selectedModel = "";
      this.formErrors.selectedRegistrationNr = "";
    },
    // new item: suggestions + rooms; otherwise the department's items of the chosen type
    refreshItemOptions() {
      if (!this.selectedEquipment) return;
      if (this.isNewItem) {
        this.loadNewItemOptions();
      } else if (this.modelsKey !== this.currentModelsKey) {
        this.fetchModels();
      }
    },
    fitLocationMenu() {
      this.locationMenuWidth = this.$refs.location.$el.offsetWidth;
    },
    async loadNewItemOptions() {
      const typeId = this.selectedEquipment;
      try {
        const [suggestions, locations] = await Promise.all([
          this.suggestionsType === typeId ? null : api.getModelSuggestions(typeId),
          this.locations.length ? null : api.fetchLocations(),
        ]);
        if (suggestions && this.selectedEquipment === typeId) {
          this.modelSuggestions = suggestions;
          this.suggestionsType = typeId;
        }
        if (locations) {
          this.locations = locations
            .filter((loc) => !loc.is_hidden)
            .map((loc) => ({ id: loc.id, label: `${loc.block}, ${loc.floor}, ${loc.room}` }))
            .sort((a, b) => a.label.localeCompare(b.label, "ro", { numeric: true }));
        }
      } catch (error) {
        console.error("Error fetching new-item options:", error);
        this.$emit("error", "Locațiile nu au putut fi încărcate. Încercați din nou.");
      }
    },
    async fetchModels() {
      const key = this.currentModelsKey;
      this.modelsKey = key; // loading or loaded for this type + department
      try {
        const modelsResponse = await api.getEquipmentModels(
          this.getUser.userId,
          this.selectedEquipment,
          this.selectedDepartment
        );
        // the type or the department changed meanwhile
        if (key !== this.currentModelsKey) return;

        if (!modelsResponse || modelsResponse.length === 0) {
          this.formErrors.selectedModel = this.typeOffersNewItem
            ? "Departamentul nu are echipamente de acest tip. Pentru unul nou alegeți o cerere din grupul «Echipament nou»."
            : "Departamentul nu are echipamente de acest tip. Cereți DTI să le adauge.";
          this.formErrors.selectedRegistrationNr = "";
          this.models = [];
          this.registrations = [];
        } else {
          this.models = modelsResponse;
          this.formErrors.selectedModel = "";
          this.formErrors.selectedRegistrationNr = "";
        }
      } catch (error) {
        console.error("Error fetching models:", error);
        if (this.modelsKey === key) this.modelsKey = null;
        this.$emit("error", "Echipamentele departamentului nu au putut fi încărcate.");
      }
    },
    // equipment can share model AND registration number: the list carries the ids, the request is filed by id
    async fetchRegistrationNr() {
      if (this.selectedModel) {
        try {
          const registrations = await api.getEquipmentRegistrationNum(
            this.getUser.userId,
            this.selectedEquipment,
            this.selectedModel,
            this.selectedDepartment,
          );
          this.registrations = registrations.map(itemChoice);
          this.formErrors.selectedModel = "";
          this.formErrors.selectedRegistrationNr = "";
        } catch (error) {
          console.error("Error fetching registrations:", error);
          this.$emit("error", "Numerele de inventar nu au putut fi încărcate.");
        }
      } else {
        this.registrations = [];
      }
    },
    validateForm() {
      this.formErrors.selectedEquipment = this.selectedEquipment
        ? ""
        : "Selectați un echipament";
      const needsOwned = !this.isNewItem;
      this.formErrors.selectedModel = !needsOwned || this.selectedModel
        ? ""
        : "Selectați un model";
      this.formErrors.selectedRegistrationNr = !needsOwned || this.selectedRegistrationNr
        ? ""
        : "Selectați un număr de înregistrare";
      if (!this.isNewItem) {
        this.formErrors.newModel = "";
      } else if (!this.newModelText) {
        this.formErrors.newModel = "Scrieți modelul";
      } else {
        this.formErrors.newModel = this.newModelText.length > 150 ? "Modelul este prea lung (maxim 150 de caractere)" : "";
      }
      this.formErrors.selectedLocation = !this.isNewItem || this.selectedLocation
        ? ""
        : "Alegeți unde se livrează";
      this.formErrors.selectedDepartment = this.selectedDepartment
        ? ""
        : "Alegeți departamentul";
      this.formErrors.selectedRequest = this.selectedRequest
        ? ""
        : "Selectați un tip de cerere";
    },
    isFormValid() {
      return !Object.values(this.formErrors).some((error) => error);
    },
    closeDialog() {
      this.dialog = false;
      this.clearFields();
    },
    clearFields() {
      this.formErrors = this.emptyErrors();
      this.selectedEquipment = null;
      this.resetOwnedItem();
      this.selectedDepartment = this.mainDepartment;
      this.selectedRequest = null;
      this.body = "";
      this.photos = [];
      this.newModel = null;
      this.newModelSearch = null;
      this.newRegistration = "";
      this.selectedLocation = null;
    },
  },
  mounted() {
    this.fetchData();
  },
  watch: {
    selectedEquipment() {
      this.formErrors.selectedEquipment = "";
    },
    selectedModel() {
      this.formErrors.selectedModel = "";
    },
    selectedRegistrationNr() {
      this.formErrors.selectedRegistrationNr = "";
    },
    selectedDepartment(newVal, oldVal) {
      this.formErrors.selectedDepartment = "";
      // another department has other equipment
      if (oldVal && newVal !== oldVal && this.selectedEquipment) {
        this.resetOwnedItem();
        this.refreshItemOptions();
      }
    },
    mainDepartment(value) {
      if (!this.selectedDepartment) this.selectedDepartment = value;
    },
    selectedRequest() {
      this.formErrors.selectedRequest = "";
      this.refreshItemOptions();
    },
    newModelSearch() {
      this.formErrors.newModel = "";
    },
    selectedLocation() {
      this.formErrors.selectedLocation = "";
    },
  },
};
</script>

<style scoped>
.dept-picker {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 10px;
  margin-bottom: 12px;
}
.dept-picker__option {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 12px;
  border: 2px solid var(--line);
  background: #fff;
  font: inherit;
  font-size: 1rem;
  font-weight: 600;
  color: var(--ink);
  text-align: left;
  cursor: pointer;
}
.dept-picker__option:hover {
  border-color: var(--ink-3);
}
.dept-picker__option.is-active {
  border-color: var(--green);
  background: var(--mint-50);
}
.dept-picker__radio.v-icon {
  color: var(--ink-3);
}
.dept-picker__option.is-active .dept-picker__radio.v-icon {
  color: var(--green);
}
.dept-picker__name {
  flex: 1;
}
.dept-picker__tag {
  padding: 1px 8px;
  border-radius: 999px;
  background: var(--pine-900);
  color: var(--leaf);
  font-size: 0.78rem;
  font-weight: 700;
}
.req-dialog__dept {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--ink-2);
  font-weight: 600;
}
.req-dialog__dept .v-icon {
  color: var(--green);
}
.cause-tag {
  flex: none;
  margin-left: 8px;
  padding: 1px 8px;
  border-radius: 999px;
  background: var(--mint-50);
  border: 1px solid var(--leaf-200);
  color: var(--green-700);
  font-size: 0.78rem;
  font-weight: 700;
  white-space: nowrap;
}
.new-item-note {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 8px;
  padding: 10px 12px;
  border-radius: 10px;
  background: var(--mint-50);
  border: 1px solid var(--leaf-200);
  color: var(--ink-2);
  font-size: 0.95rem;
  line-height: 1.4;
}
.new-item-note .v-icon {
  margin-top: 2px;
  color: var(--green);
}
.reg-select ::v-deep .v-select__selections {
  flex-wrap: nowrap;
}
.reg-select ::v-deep .v-select__selections input {
  min-width: 0 !important;
}
/* the list is as wide as the field: long placements wrap instead of being cut */
.reg-choice__line {
  white-space: normal;
  line-height: 1.35;
}
.selected-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  display: inline-block;
}
</style>
